/**
 * useQueueByReservationId / useQueueActions — batched cross-reservation
 * waitlist data + queue mutations for the /reservations route (CU-86cb7ctfw).
 *
 * Extracted from ReservationsPage (mirrors the existing useReservationActions /
 * useReservationPermissions hook-extraction pattern already used by this
 * feature) so the route component stays within the max-lines-per-function
 * budget. Split into two hooks — data fetch/mapping vs. mutation dispatch —
 * so neither grows past that same budget itself.
 */
import * as React from 'react';
import { reservationQueue } from '@ooh/contracts';
import type { QueueEntryGroup } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { queueByReservationsRequest } from '../api/queries';
import { useLeaveWaitlist, useAdminDisplaceAndPromote } from '../api/queue-mutations';
import type { QueueAction, QueueEntryView } from '../components/reservation-queue-list';
import { mapQueueEntriesToViews } from '../lib/queue-entry-view';

export interface QueuePanelWindow {
  mediaUnitId: number;
  dateFrom: string;
  dateTo: string;
}

export interface QueuePanelRow extends QueuePanelWindow {
  id: number;
}

/**
 * Maps the batched GET /reservations/queue/by-reservations response to the
 * `queueByReservationId` shape ReservationListView expects.
 *
 * An entry is flagged `spansMultiple` when it overlaps more than one of the
 * page's reservations on the SAME unit; reservations on other units can never
 * share a queue entry, so they are not siblings.
 */
export function mapQueueGroupsToViews(
  groups: QueueEntryGroup[],
  reservationsById: Map<number, QueuePanelWindow>,
): Record<number, QueueEntryView[]> {
  const result: Record<number, QueueEntryView[]> = {};
  for (const group of groups) {
    const res = reservationsById.get(group.reservationId);
    if (!res) continue;
    const siblings = [...reservationsById.values()].filter(
      (other) => other.mediaUnitId === res.mediaUnitId,
    );
    result[group.reservationId] = mapQueueEntriesToViews(group.entries, res, siblings);
  }
  return result;
}

export interface UseQueueByReservationIdResult {
  queueByReservationId: Record<number, QueueEntryView[]>;
  mediaUnitIdByReservationId: Map<number, number>;
}

/**
 * Fetches the batched queue for the current page's reservation ids and builds
 * the `queueByReservationId` map ReservationListView expects, plus a
 * reservationId -> mediaUnitId lookup for the mutation hooks (which need a
 * mediaUnitId for cache invalidation, not a reservationId).
 *
 * `rows` MUST be non-empty: the batched endpoint requires at least one id, and
 * a Suspense query cannot be disabled.
 */
export function useQueueByReservationId(rows: QueuePanelRow[]): UseQueueByReservationIdResult {
  const pageReservationIds = React.useMemo(() => rows.map((r) => r.id), [rows]);
  const { data: queueGroupsData } = useApiSuspenseQuery(
    reservationQueue.listByReservations,
    queueByReservationsRequest(pageReservationIds),
  );
  const reservationWindowById = React.useMemo(() => {
    const map = new Map<number, QueuePanelWindow>();
    for (const row of rows) {
      map.set(row.id, { mediaUnitId: row.mediaUnitId, dateFrom: row.dateFrom, dateTo: row.dateTo });
    }
    return map;
  }, [rows]);
  const queueByReservationId = React.useMemo(
    () => mapQueueGroupsToViews(queueGroupsData.groups, reservationWindowById),
    [queueGroupsData, reservationWindowById],
  );
  const mediaUnitIdByReservationId = React.useMemo(() => {
    const map = new Map<number, number>();
    for (const row of rows) map.set(row.id, row.mediaUnitId);
    return map;
  }, [rows]);

  return { queueByReservationId, mediaUnitIdByReservationId };
}

export interface UseQueueActionsResult {
  promoteTarget: { reservationId: number; entryId: number } | null;
  setPromoteTarget: (target: { reservationId: number; entryId: number } | null) => void;
  handleQueueAction: (action: QueueAction, entryId: number, reservationId: number) => void;
  handlePromoteConfirm: () => void;
  isPromotePending: boolean;
}

/** Owns the leave/remove/displace-and-promote mutation dispatch and the promote confirmation dialog's target state. */
export function useQueueActions(
  mediaUnitIdByReservationId: Map<number, number>,
): UseQueueActionsResult {
  const leaveWaitlist = useLeaveWaitlist();
  const adminDisplaceAndPromote = useAdminDisplaceAndPromote();

  const [promoteTarget, setPromoteTarget] = React.useState<{
    reservationId: number;
    entryId: number;
  } | null>(null);

  const handleQueueAction = React.useCallback(
    (action: QueueAction, entryId: number, reservationId: number) => {
      if (action === 'leave' || action === 'remove') {
        leaveWaitlist.mutate({
          id: entryId,
          mediaUnitId: mediaUnitIdByReservationId.get(reservationId),
        });
      } else if (action === 'promote') {
        // CU-86cb3x6y6/CU-86cb7ctfw: displace-and-promote force-releases someone
        // else's active reservation — confirm before mutating.
        setPromoteTarget({ reservationId, entryId });
      }
    },
    [leaveWaitlist, mediaUnitIdByReservationId],
  );

  const handlePromoteConfirm = React.useCallback(() => {
    if (!promoteTarget) return;
    adminDisplaceAndPromote.mutate(
      {
        id: promoteTarget.entryId,
        mediaUnitId: mediaUnitIdByReservationId.get(promoteTarget.reservationId),
      },
      { onSuccess: () => setPromoteTarget(null) },
    );
  }, [adminDisplaceAndPromote, promoteTarget, mediaUnitIdByReservationId]);

  return {
    promoteTarget,
    setPromoteTarget,
    handleQueueAction,
    handlePromoteConfirm,
    isPromotePending: adminDisplaceAndPromote.isPending,
  };
}
